import { useEffect, useState } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import { getUnfiledAssetsTotalFromCache } from 'framework-listing-lib/internal/Folders/utils/unfiledAssets';
import useUnfiledAssetVariables from 'framework-listing-lib/internal/Folders/hooks/useUnfiledAssetVariables';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { UNFILED_ASSETS_ID } from 'framework-listing-lib/internal/Folders/constants/folders';
import { ASSET_ADDED_TO_FOLDER } from 'framework-listing-lib/constants/folderEvents';
import { DELETE_OBJECTS } from 'framework-listing-lib/constants/tableEvents';
import useCurrentFolderId from 'framework-listing-lib/internal/Folders/hooks/useCurrentFolderId';
import useFetchUnfiledAssets from './useFetchUnfiledAssets';
import useMaybeApolloClient from './useMaybeApolloClient';
function modifyUnfiledAssets(previousFolderId, newFolderId, affectedAssets, currentTotal) {
  if (currentTotal != null && previousFolderId === UNFILED_ASSETS_ID) {
    // An asset is being removed from the unfiled asset folder
    return Math.max(0, currentTotal - affectedAssets);
  } else if (currentTotal != null && newFolderId === UNFILED_ASSETS_ID) {
    // An asset is being added from the unfiled asset folder
    return currentTotal + affectedAssets;
  }
  return currentTotal;
}
export default function useUnfiledAssetTotal() {
  const [total, setTotal] = useState(null);
  const client = useDataFetchingClient();
  const maybeApolloClient = useMaybeApolloClient();
  const variables = useUnfiledAssetVariables();
  useFetchUnfiledAssets();
  const unfiledAssetTotal = getUnfiledAssetsTotalFromCache({
    dfcClient: client,
    maybeApolloClient,
    variables
  });
  const currentFolderId = useCurrentFolderId();

  /*
   * Set the total unfiled assets only when the cache is populated for the first time.
   */
  useEffect(() => {
    if (total !== null) {
      return;
    }
    if (unfiledAssetTotal !== null) {
      setTotal(unfiledAssetTotal);
    }
  }, [total, unfiledAssetTotal]);

  /*
   * Set the unfiled asset total when an asset has been moved.
   */
  useEffect(() => {
    const handleUnfiledAssetChange = response => {
      if (response.newFolderId !== UNFILED_ASSETS_ID && response.previousFolderId !== UNFILED_ASSETS_ID) {
        return;
      }
      setTotal(prevTotal => modifyUnfiledAssets(response.previousFolderId, response.newFolderId, response.selectedObjectsCount != null ? response.selectedObjectsCount : response.assetIds.length, prevTotal));
    };
    listingLibObserver.on(ASSET_ADDED_TO_FOLDER, handleUnfiledAssetChange);
    return () => {
      listingLibObserver.off(ASSET_ADDED_TO_FOLDER, handleUnfiledAssetChange);
    };
  });

  /*
   * Set the unfiled asset total when an asset has been deleted.
   */
  useEffect(() => {
    const handleUnfiledAssetChange = objectIdOrIds => {
      if (currentFolderId !== UNFILED_ASSETS_ID) {
        return;
      }
      if (unfiledAssetTotal !== null) {
        setTotal(Math.max(unfiledAssetTotal - (Array.isArray(objectIdOrIds) ? objectIdOrIds.length : 1), 0));
      }
    };
    listingLibObserver.on(DELETE_OBJECTS, handleUnfiledAssetChange);
    return () => {
      listingLibObserver.off(DELETE_OBJECTS, handleUnfiledAssetChange);
    };
  });
  return total || 0;
}